<template>
  <div
    :class="{
      container: true,
      'no-images': noImagesAtThisLocation
    }"
    ref="container"
    @click="highlight"
  >
    <canvas
      :style="{ cursor: hoverEnabled && hovering ? 'pointer' : 'default' }"
      ref="canvas"
      @mousemove="hover"
      @mouseleave="mouseleave"
    ></canvas>
    <div v-if="noImagesAtThisLocation" class="no-images-message">
      <div class="no-images-message-text">
        <div>This shoot does not have images at this location.</div>
        <div class="no-images-links">
          <div class="no-images-link" v-if="previousImageLocation">
            <div @click="goToPreviousImage">&lt; Go to previous image</div>
          </div>
          <div class="no-images-link" v-if="nextImageLocation">
            <div @click="goToNextImage">Go to next image &gt;</div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="center-image"
      :style="{
        left: centerImageLeft + 'px',
        width: centerImageWidth + 'px'
      }"
    />
  </div>
</template>

<script>
import * as d3 from "d3";
import { ResizeObserver } from "@juggle/resize-observer";

const SCALE = 2;

export default {
  name: "ImageFlipper",
  props: {
    upsidedown: Boolean,
    data: Array,
    index: Number,
    parent: null,
    n: Number,
    side: String
  },
  data() {
    let h = this.parent ? this.parent.clientHeight : window.innerHeight / 2;

    return {
      width: 1000,
      height: (h - 10 * (this.n - 1)) / this.n,
      hovering: false,
      hoverX: null,
      requestedImages: []
    };
  },

  mounted() {
    this.loadImages();
    this.render();

    const ro = new ResizeObserver(this.resize);
    ro.observe(this.$el);
    this.resizeObserver = ro;
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs.container);
  },
  computed: {
    noImagesAtThisLocation() {
      return (
        this.images.length === 0 &&
        (this.nextImageLocation || this.previousImageLocation)
      );
    },

    loadedImages() {
      return this.$store.state.loadedImages;
    },
    mode() {
      return this.$store.state.imageFlipper.mode;
    },
    modeBlended() {
      return this.mode.indexOf("blend") === 0;
    },
    modePyramid() {
      return this.mode.indexOf("pyramid") >= 0;
    },
    blendPercentage() {
      return this.$store.state.imageFlipper.blendPercentage;
    },
    visibleDistance() {
      return this.$store.state.imageFlipper.visibleDistance;
    },
    w() {
      return (250 / 180) * 1.0 * this.height;
    },
    targetHeight() {
      let h = this.parent ? this.parent.clientHeight : window.innerHeight / 2;

      return (h - 10 * (this.n - 1)) / this.n;
    },
    h() {
      return this.height;
    },

    bottomScale() {
      return this.$store.state.imageFlipper.bottomScale;
    },

    topScale() {
      return this.$store.state.imageFlipper.topScale;
    },

    scale() {
      let scale = !this.upsidedown ? this.topScale : this.bottomScale;
      if (!scale) {
        scale = this.$store.getters.getScale(this.width, this.height);
      }

      if (this.upsidedown) {
        this.$store.dispatch("setBottomScale", scale);
      } else {
        this.$store.dispatch("setTopScale", scale);
      }
      return scale;
    },
    location() {
      return this.$store.state.geolocation.location;
    },

    imageBuffer() {
      return Math.abs(this.scale.invert(window.innerWidth * 1.5));
    },

    images() {
      let data = this.data;
      let location = this.location;
      let totalDistance = this.$store.getters.totalDistance;

      let images = data
        .map(p => ({
          d: p.location - location * totalDistance,
          ...p
        }))
        .filter(p => Math.abs(p.d) < this.imageBuffer);

      if (this.modePyramid) {
        images = images.sort((b, a) => Math.abs(a.d) - Math.abs(b.d));
      }

      return images;
    },

    imagesToStartLoading() {
      if (this.$store.state.geolocation.jumping) return [];

      return this.images.filter(
        image => !this.$store.state.images[image.id].startedLoad
      );
    },

    imagesToCancelLoading() {
      let location = this.location;
      let totalDistance = this.$store.getters.totalDistance;

      let cancelImages = this.requestedImages
        .filter(({ id }) => !this.$store.state.images[id].loaded)
        .map(image => ({
          ...image,
          d: image.location - location * totalDistance
        }))
        .filter(image => Math.abs(image.d) > this.imageBuffer);
      return cancelImages;
    },

    hoverEnabled() {
      return this.$route.name !== "narrative";
    },
    centerImageIndex() {
      if (!this.images.length) return -1;

      const distances = this.images.map(i => Math.abs(i.d));
      const lowestD = d3.min(distances);
      return distances.indexOf(lowestD);
    },
    centerImage() {
      if (this.centerImageIndex < 0) return null;
      return this.images[this.centerImageIndex];
    },
    centerImageLeft() {
      if (!this.centerImage) return 0;
      return -this.w / 2 + this.scale(this.centerImage.d);
    },
    centerImageWidth() {
      return this.w;
    },
    locationsNormalized() {
      const total = this.$store.getters.totalDistance;
      return this.data
        .map(({ location }) => location / total)
        .sort((a, b) => a - b);
    },
    previousImageLocation() {
      let result;
      if (this.$store.state.geolocation.flipped) {
        result = this.locationsNormalized
          .filter(l => l < this.location)
          .slice(-1)[0];
      } else {
        result = this.locationsNormalized.filter(l => l > this.location)[0];
      }
      return result;
    },
    nextImageLocation() {
      let result;
      if (this.$store.state.geolocation.flipped) {
        result = this.locationsNormalized.filter(l => l > this.location)[0];
      } else {
        result = this.locationsNormalized
          .filter(l => l < this.location)
          .slice(-1)[0];
      }
      return result;
    }
  },
  methods: {
    resize() {
      if (!this.$refs.container) return;

      this.height = Math.max(this.$el.clientHeight, this.targetHeight * 0.5);
      this.width = this.$el.clientWidth;
      this.$refs.canvas.setAttribute("height", this.height);
      this.$refs.canvas.setAttribute("width", this.width);

      if (this.index === 0) {
        const scale = this.$store.getters.getScale(this.width, this.height);
        if (this.upsidedown) {
          this.$store.dispatch("setBottomScale", scale);
        } else {
          this.$store.dispatch("setTopScale", scale);
        }
      }

      this.draw();
    },

    render() {
      if (this.$refs.canvas) {
        requestAnimationFrame(this.draw);
      }
    },

    draw() {
      if (!this.$refs.canvas) return;
      let ctx = this.$refs.canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, this.width, this.height);

      this.images.forEach(p => {
        this.drawImage(ctx, p, { border: false });
      });

      // Render highlighted image on top. We need to do this specifically in
      // situations where the highlighted image would not be fully visible
      // because it is not centered.
      if (this.hovering) {
        this.drawImage(ctx, this.hoverImage, { border: true });
        // this.drawImage(this.images[this.centerImageIndex], { border: true });
      }
    },

    drawImage(ctx, p, opts) {
      if (!ctx || !p) return;
      opts = opts || {};
      let border = opts.border || false;
      let upsidedown = this.upsidedown || false;
      let d = p.d;
      let scale = this.scale;
      let image = this.$store.state.images[p.id];

      if (this.hoverEnabled && border && this.hovering) {
        ctx.shadowColor = "black";
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        ctx.strokeStyle = "#000";

        if (image.loaded && image.image) {
          ctx.lineWidth = 4;
        } else {
          ctx.lineWidth = 1;
        }
        ctx.globalAlpha = 1;
        ctx.strokeRect(
          -this.w / 2 + scale(d) + 3.5,
          this.height - this.h + 1,
          this.w - 6,
          this.h - 2
        );
        ctx.shadowColor = "transparent";
      }

      if (!image.image || !image.loaded) {
        ctx.fillStyle = "#eee";
        ctx.globalAlpha = 0.5;
        ctx.fillRect(
          -this.w / 2 + scale(d),
          this.height - this.h,
          this.w,
          this.h
        );
      } else {
        ctx.globalAlpha = 1;
        if (upsidedown) {
          ctx.translate(scale(d), this.height - this.h / 2);
          ctx.rotate(Math.PI);

          const drawImageArgs = [
            Math.floor(252 * SCALE),
            Math.floor(168 * SCALE),
            Math.floor(-248 * SCALE),
            Math.floor(-164 * SCALE),
            Math.floor(-this.w / 2),
            Math.floor(-this.h / 2),
            Math.floor(this.w),
            Math.floor(this.h)
          ];

          if (border && this.hovering) {
            ctx.drawImage(image.image, ...drawImageArgs);
          } else {
            if (this.modeBlended) {
              if (!image.transparentImage) {
                image.transparentImage = this.getImageWithTransparency(
                  image.image
                );
              }

              if (image.transparentImage) {
                ctx.drawImage(image.transparentImage, ...drawImageArgs);
              }
            } else {
              ctx.drawImage(image.image, ...drawImageArgs);
            }
          }
          ctx.rotate(-Math.PI);
          ctx.translate(-scale(d), -(this.height - this.h / 2));
        } else {
          const drawImageArgs = [
            Math.floor(4 * SCALE),
            Math.floor(4 * SCALE),
            Math.floor(248 * SCALE),
            Math.floor(164 * SCALE),
            Math.floor(-this.w / 2 + scale(d)),
            Math.floor(this.height - this.h),
            Math.floor(this.w),
            Math.floor(this.h)
          ];

          if (border && this.hovering) {
            ctx.drawImage(image.image, ...drawImageArgs);
          } else {
            if (this.modeBlended) {
              if (!image.transparentImage) {
                image.transparentImage = this.getImageWithTransparency(
                  image.image
                );
              }
              if (image.transparentImage) {
                ctx.drawImage(image.transparentImage, ...drawImageArgs);
              }
            } else {
              ctx.drawImage(image.image, ...drawImageArgs);
            }
          }
        }
      }
    },

    getImageWithTransparency(image) {
      const transparencyCanvas = document.createElement("canvas");
      if (!transparencyCanvas) return null;

      const transparencyContext = transparencyCanvas.getContext("2d");
      if (!transparencyContext) return null;

      transparencyCanvas.width = image.width;
      transparencyCanvas.height = image.height;

      transparencyContext.drawImage(image, 0, 0);

      // Gradient mask
      var gradient = transparencyContext.createLinearGradient(
        0,
        0,
        transparencyCanvas.width,
        0
      );
      gradient.addColorStop(0, "rgba(255,255,255,1)");
      gradient.addColorStop(this.blendPercentage, "rgba(255,255,255,0)");
      gradient.addColorStop(1 - this.blendPercentage, "rgba(255,255,255,0)");
      gradient.addColorStop(1, "rgba(255,255,255,1)");

      transparencyContext.fillStyle = gradient;
      transparencyContext.globalCompositeOperation = "destination-out";

      transparencyContext.fillRect(
        0,
        0,
        transparencyCanvas.width,
        transparencyCanvas.height
      );

      return transparencyCanvas;
    },

    highlight() {
      if (!this.hoverEnabled) return;

      if (this.hovering && this.hoverImage) {
        this.$router.push({
          name: "MapModal",
          params: { image: this.hoverImage.id },
          query: this.$route.query
        });
      }
    },

    hover(e) {
      if (!this.hoverEnabled) return;
      this.hoverX = e.offsetX;
      this.updateHoverState();
    },

    updateHoverState() {
      if (!this.hoverEnabled) return;
      this.hovering = true;

      if (this.centerImage && this.modePyramid) {
        let x1 = -this.w / 2 + this.scale(this.centerImage.d);
        let x2 = x1 + this.w;

        if (this.hoverX >= x1 && this.hoverX <= x2) {
          this.hoverImage = this.centerImage;
        } else if (this.hoverX < this.width / 2) {
          // Handle left side
          this.hoverImage = this.images
            .filter(i => this.scale(i.d) - this.w / 2 <= this.hoverX)
            .sort((a, b) => this.scale(b.d) - this.scale(a.d))[0];
        } else {
          // Handle right side
          this.hoverImage = this.images
            .filter(i => this.scale(i.d) + this.w / 2 >= this.hoverX)
            .sort((a, b) => this.scale(a.d) - this.scale(b.d))[0];
        }
      } else {
        this.hoverImage = this.images
          .filter(i => this.scale(i.d) - this.w / 2 <= this.hoverX)
          .sort((a, b) => this.scale(b.d) - this.scale(a.d))[0];
      }
    },

    mouseleave() {
      this.hovering = false;
      this.hoverX = null;
      this.hoverImage = null;
    },

    goToPreviousImage() {
      this.$store.dispatch("goDirectlyToLocation", {
        target: this.previousImageLocation
      });
    },

    goToNextImage() {
      this.$store.dispatch("goDirectlyToLocation", {
        target: this.nextImageLocation
      });
    },

    loadImages() {
      // Stop loading images that we no longer need
      const cancelImages = this.imagesToCancelLoading;
      cancelImages.forEach(image => {
        this.$store.dispatch("cancelLoadImage", image.id);
      });
      const cancelImagesIds = cancelImages.map(({ id }) => id);

      let requestedImagesUpdated = this.requestedImages.filter(
        image => cancelImagesIds.indexOf(image.id) < 0
      );

      // Load images that haven't been loaded yet
      this.imagesToStartLoading.forEach(image => {
        this.$store.dispatch("loadImage", image.id);
      });

      requestedImagesUpdated = [
        ...requestedImagesUpdated,
        ...this.imagesToStartLoading
      ];

      this.requestedImages = requestedImagesUpdated;
    }
  },
  watch: {
    animate() {
      this.resize();
    },

    blendPercentage() {
      // Clear cached transparentImage if blend percentage changes
      this.images
        .map(({ id }) => id)
        .forEach(id => (this.$store.state.images[id].transparentImage = null));
      this.render();
    },

    hovering() {
      this.render();
    },

    hoverX() {
      this.render();
    },

    images() {
      this.loadImages();

      if (this.hovering) {
        this.updateHoverState();
      }
      this.render();
    },

    loadedImages() {
      this.render();
    },

    location() {
      this.render();
    },

    mode() {
      this.render();
    },

    visibleDistance() {
      this.resize();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/constants/_variables.scss";

.container {
  background: #f0f0f0;
  height: 100%;
  width: 100%;
  position: relative;

  canvas {
    position: absolute;
    pointer-events: all;
    left: 0px;
    top: 0px;
    height: 100%;
    width: 100%;
  }
}

.v-tour--active .container canvas {
  pointer-events: none;
}

.center-image {
  bottom: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
}

.no-images-message {
  display: flex;
  flex-direction: column;
  font-size: 20px;
  height: 100%;
  justify-content: space-around;
  transform: rotate(0deg);
}

.no-images-links {
  display: flex;
  justify-content: center;
}

.no-images-link {
  margin-top: 5px;
  margin-left: 10px;

  &:first-of-type {
    margin-left: 0;
  }

  div {
    color: $copper;
    cursor: pointer;
    font-weight: 500;
    text-decoration: underline;

    &:hover {
      color: black;
    }
  }
}

.flipped {
  .no-images-message {
    transform: rotate(180deg);
  }
}
</style>
